import { compareNtpShort, isNtpShort } from "@blockcast/mmt-base";
import { carriesMedia, endpointKey, selectMulticastTier, type MulticastTierEndpoint } from "./multicast-tier.js";

export type OrchestratorState = "stable" | "joining";
export type OrchestratorCommand =
	| { kind: "join-target"; current: MulticastTierEndpoint; target: MulticastTierEndpoint }
	| { kind: "commit-swap"; target: MulticastTierEndpoint; old: MulticastTierEndpoint }
	| { kind: "abandon-join"; current: MulticastTierEndpoint; target: MulticastTierEndpoint };

export interface MulticastTierPacket {
	endpointKey: string;
	keyframe: boolean;
	/**
	 * The packet's MMTP header `timestamp`: its UTC sending instant in NTP short
	 * format (ISO/IEC 23008-1 §9.2.2), which wraps every 2^16 s (~18.2 h). The
	 * orchestrator orders these only with RFC 1982 serial arithmetic
	 * (`compareNtpShort`), never with `<` or `Math.max`.
	 *
	 * A non-finite value means the host could not read a header; that packet
	 * neither raises the high-water mark nor commits. Any other value that is
	 * not NTP short is a host bug and throws a RangeError.
	 */
	timestamp: number;
	hasValidCodecConfig: boolean;
}

export interface OrchestratorOptions {
	endpoints: readonly MulticastTierEndpoint[];
	initialEndpoint?: MulticastTierEndpoint;
	adaptive?: boolean;
	now: () => number;
	dwellMs?: number;
	joinDeadlineMs?: number;
}

export interface GoodputSample { bitrate: number | undefined; }
export interface HeadroomSample { healthy: boolean; }

export const DEFAULT_DWELL_MS = 3_000;
export const DEFAULT_JOIN_DEADLINE_MS = 5_000;

export function goodputFromStats(
	previous: { bytesReceived: number; atMs: number } | undefined,
	current: { bytesReceived: number; atMs: number },
	minSampleMs = 500,
): number | undefined {
	if (!previous) return undefined;
	const durationMs = current.atMs - previous.atMs;
	const bytes = current.bytesReceived - previous.bytesReceived;
	if (durationMs < minSampleMs || bytes <= 0) return undefined;
	return (bytes * 8 * 1000) / durationMs;
}

export class MulticastOrchestrator {
	#state: OrchestratorState = "stable";
	#current: MulticastTierEndpoint | undefined;
	#target: MulticastTierEndpoint | undefined;
	#estimate: number | undefined;
	#favored: { key: string; since: number; source: "goodput" | "headroom" } | undefined;
	#joinStartedAt = 0;
	/**
	 * Latest active-tier send time (NTP short), or undefined before the first.
	 * "Latest" is serial order, not the numerically largest value: after the
	 * field wraps, the largest value is the stale one.
	 */
	#highWater: number | undefined;
	readonly #dwellMs: number;
	readonly #joinDeadlineMs: number;

	constructor(private readonly options: OrchestratorOptions) {
		this.#dwellMs = options.dwellMs ?? DEFAULT_DWELL_MS;
		this.#joinDeadlineMs = options.joinDeadlineMs ?? DEFAULT_JOIN_DEADLINE_MS;
		if (!Number.isFinite(this.#dwellMs) || !Number.isFinite(this.#joinDeadlineMs) ||
			this.#dwellMs <= 0 || this.#joinDeadlineMs <= 0) {
			throw new Error("Multicast dwell and join deadline must be positive");
		}
	}

	get state(): OrchestratorState { return this.#state; }
	get current(): MulticastTierEndpoint | undefined { return this.#current; }
	get target(): MulticastTierEndpoint | undefined { return this.#target; }
	get estimate(): number | undefined { return this.#estimate; }

	start(): MulticastTierEndpoint | undefined {
		this.#current ??= this.options.initialEndpoint ?? selectMulticastTier(this.options.endpoints, undefined);
		return this.#current;
	}

	onGoodput(sample: GoodputSample): OrchestratorCommand | undefined {
		const bitrate = sample.bitrate;
		if (!this.#current) return undefined;
		// Before the adaptive gate: a selected join runs with adaptation off, and a
		// target that delivers nothing reaches its deadline only on this clock.
		if (this.#state === "joining") return this.poll();
		if (this.options.adaptive === false) return undefined;
		if (bitrate === undefined || !Number.isFinite(bitrate)) return undefined;
		this.#estimate = bitrate;
		const desired = selectMulticastTier(this.options.endpoints, bitrate);
		if (!desired) return undefined;
		const desiredKey = endpointKey(desired);
		if (desiredKey === endpointKey(this.#current)) {
			if (this.#favored?.source === "goodput") this.#favored = undefined;
			return undefined;
		}
		if (typeof desired.bandwidth !== "number" || typeof this.#current.bandwidth !== "number" ||
			desired.bandwidth >= this.#current.bandwidth) {
			if (this.#favored?.source === "goodput") this.#favored = undefined;
			return undefined;
		}

		const now = this.#now();
		if (!this.#favored || this.#favored.key !== desiredKey || this.#favored.source !== "goodput") {
			this.#favored = { key: desiredKey, since: now, source: "goodput" };
			return undefined;
		}
		if (now - this.#favored.since < this.#dwellMs) return undefined;

		this.#state = "joining";
		this.#target = desired;
		this.#joinStartedAt = now;
		this.#favored = undefined;
		return { kind: "join-target", current: this.#current, target: desired };
	}

	onHeadroom(sample: HeadroomSample): OrchestratorCommand | undefined {
		if (!this.#current || this.options.adaptive === false || this.#state === "joining") return undefined;
		if (!sample.healthy) {
			if (this.#favored?.source === "headroom") this.#favored = undefined;
			return undefined;
		}
		if (this.#favored?.source === "goodput") return undefined;

		const currentBandwidth = this.#current.bandwidth;
		if (typeof currentBandwidth !== "number") return undefined;
		// Ranked here rather than through selectMulticastTier: up-selection
		// advances exactly one adjacent rung, where the selector fits a budget to
		// an estimate. The media filter is shared so a repair-only endpoint above
		// the current rung is not joined as an upgrade, which would swap the
		// receiver onto repair symbols and no media.
		const desired = this.options.endpoints
			.filter((endpoint) =>
				carriesMedia(endpoint) && typeof endpoint.bandwidth === "number" && endpoint.bandwidth > currentBandwidth,
			)
			.sort((a, b) => a.bandwidth! - b.bandwidth!)[0];
		if (!desired) {
			this.#favored = undefined;
			return undefined;
		}

		const desiredKey = endpointKey(desired);
		if (desiredKey === endpointKey(this.#current)) {
			this.#favored = undefined;
			return undefined;
		}
		const now = this.#now();
		if (!this.#favored || this.#favored.key !== desiredKey) {
			this.#favored = { key: desiredKey, since: now, source: "headroom" };
			return undefined;
		}
		if (now - this.#favored.since < this.#dwellMs) return undefined;

		this.#state = "joining";
		this.#target = desired;
		this.#joinStartedAt = now;
		this.#favored = undefined;
		return { kind: "join-target", current: this.#current, target: desired };
	}

	/**
	 * Join `target` because the viewer selected it, not because goodput or
	 * headroom ranked it. It runs whether or not the orchestrator adapts, and
	 * takes the path an adaptive join takes: commit on the target's RAP at or
	 * beyond the high-water mark, abandon at the join deadline. While it is
	 * pending nothing re-ranks the ladder; goodput only advances the deadline.
	 *
	 * Returns undefined, changing nothing, while another join is pending (cancel
	 * it first), when `target` is the current tier, or when it carries no media.
	 */
	select(target: MulticastTierEndpoint): OrchestratorCommand | undefined {
		if (!this.#current || this.#state === "joining" || !carriesMedia(target)) return undefined;
		if (endpointKey(target) === endpointKey(this.#current)) return undefined;
		this.#state = "joining";
		this.#target = target;
		this.#joinStartedAt = this.#now();
		this.#favored = undefined;
		return { kind: "join-target", current: this.#current, target };
	}

	/**
	 * Feed a packet from the active tier (to advance the high-water mark) or
	 * from the in-flight target (to find a committable RAP).
	 *
	 * Serial order is only the true order while the compared instants are less
	 * than `NTP_SHORT_HALF_RANGE` (2^15 s, ~9.1 h) apart. Feeding every
	 * active-tier packet keeps the mark that close to the live stream. A mark
	 * refreshed only by commits can fall further behind than that, and a
	 * target RAP may then compare as older, so the join is abandoned.
	 */
	onTierPacket(packet: MulticastTierPacket): OrchestratorCommand | undefined {
		if (!this.#current) return undefined;
		const sentAt = tierPacketSendTime(packet);
		// An instant exactly half the space from the mark (RFC 1982's undefined
		// case) is neither later nor earlier, so it leaves the mark alone, as a
		// reordered earlier packet does.
		if (packet.endpointKey === endpointKey(this.#current) && sentAt !== undefined &&
			(this.#highWater === undefined || compareNtpShort(sentAt, this.#highWater) === "after")) {
			this.#highWater = sentAt;
		}
		if (this.#state !== "joining" || !this.#target) return undefined;
		const expired = this.poll();
		if (expired) return expired;
		if (
			packet.endpointKey !== endpointKey(this.#target) ||
			!packet.keyframe ||
			!packet.hasValidCodecConfig ||
			sentAt === undefined ||
			!this.#reachesHighWater(sentAt)
		) return undefined;

		const command: OrchestratorCommand = {
			kind: "commit-swap",
			target: this.#target,
			old: this.#current,
		};
		this.#highWater = sentAt;
		this.#current = this.#target;
		this.#target = undefined;
		this.#state = "stable";
		return command;
	}

	poll(): OrchestratorCommand | undefined {
		if (this.#state !== "joining" || !this.#target || !this.#current) return undefined;
		if (this.#now() - this.#joinStartedAt < this.#joinDeadlineMs) return undefined;
		return this.cancel();
	}

	cancel(): OrchestratorCommand | undefined {
		if (this.#state !== "joining" || !this.#target || !this.#current) return undefined;
		const command: OrchestratorCommand = {
			kind: "abandon-join",
			current: this.#current,
			target: this.#target,
		};
		this.#target = undefined;
		this.#state = "stable";
		return command;
	}

	/**
	 * Whether a target RAP sent at `sentAt` is at or beyond the active tier's
	 * high-water mark. A pair exactly half the NTP-short space apart has no
	 * serial order, so it cannot show that and does not commit.
	 */
	#reachesHighWater(sentAt: number): boolean {
		if (this.#highWater === undefined) return true;
		const order = compareNtpShort(sentAt, this.#highWater);
		return order === "after" || order === "equal";
	}

	#now(): number {
		const now = this.options.now();
		if (!Number.isFinite(now)) throw new Error("Multicast orchestrator clock must be finite");
		return now;
	}
}

/** A tier packet's NTP-short send time, or undefined when the host had no header to read. */
function tierPacketSendTime(packet: MulticastTierPacket): number | undefined {
	if (!Number.isFinite(packet.timestamp)) return undefined;
	if (!isNtpShort(packet.timestamp)) {
		throw new RangeError(
			`Multicast tier packet timestamp must be an MMTP NTP-short send time, got ${packet.timestamp}`,
		);
	}
	return packet.timestamp;
}
